<script setup>
import { FORM_TYPE, FORM_STATUS } from "@/constants";
import { reactive, ref } from "vue";
import {
  queryCustomFormById,
  insertCustomForm,
  updateCustomForm,
} from "@/api/customForm";
import { ElMessage } from "element-plus";
import useSystemList from "../hooks/useSystemList";

// 系统列表
const { systemList } = useSystemList();

// 事件传递
const emits = defineEmits(["afterSave"]);

// 弹窗控制器
const visible = ref(false);

// 表单ref
const ruleRef = ref(null);

// 提交按钮loading
const buttonLoading = ref(false);

// 新增编辑函数对象
const adjustFunc = reactive({
  add: insertCustomForm,
  edit: updateCustomForm,
});

// 临时存放所属系统code
const serviceCode = ref(null);

// 表单对象
const formData = ref({
  id: null,
  formKey: null,
  formName: null,
  serviceCode: null,
  formType: null,
  remark: null,
  state: "1",
  formUrl: null,
});

// 表单校验对象
const rules = reactive({
  formKey: [
    { required: true, message: "请输入表单key", trigger: "blur" },
    { pattern: /^\w+$/, message: "请输入字母/数字/下划线", trigger: "blur" },
  ],
  formName: [{ required: true, message: "请输入表单名", trigger: "blur" }],
  systemId: [{ required: true, message: "请选择所属系统", trigger: "change" }],
  formType: [{ required: true, message: "请选择表单类型", trigger: "change" }],
  state: [{ required: true, message: "请选择表单状态", trigger: "change" }],
});

// 弹窗参数对象
const dialogConfig = ref({});

// 表单打开
const handleDialogOpen = (config) => {
  dialogConfig.value = { ...config };
  visible.value = true;
  ruleRef.value?.resetFields();
  formData.value = {
    id: null,
    formKey: null,
    formName: null,
    serviceCode: null,
    formType: null,
    remark: null,
    state: "1",
    formUrl: null,
  };
  if (config.type == "edit") {
    queryDetailInfo(config.row.id);
  }
};

// 点击确定按钮
const handleSubmit = () => {
  buttonLoading.value = true;
  ruleRef.value?.validate((valid) => {
    if (valid) {
      adjustFunc[dialogConfig.value.type]({ ...formData.value })
        .then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            visible.value = false;
            emits("afterSave");
          }
        })
        .finally(() => {
          buttonLoading.value = false;
        });
    } else {
      buttonLoading.value = false;
    }
  });
};

// 通过id查询详情
const queryDetailInfo = (id) => {
  queryCustomFormById(id).then((res) => {
    const arr = [
      "id",
      "formKey",
      "formName",
      "remark",
      "formUrl",
      "serviceCode",
    ];
    arr.forEach((key) => {
      formData.value[key] = res.data[key];
    });
    formData.value.formType = res.data.formType.value;
    formData.value.state = res.data.state.value;
    serviceCode.value = res.data?.serviceCode
      ?.split(",")
      ?.map((i) => (i = i * 1));
  });
};

// 所属系统变化
const systemChange = (val) => {
  const result = val.join(",");
  formData.value.serviceCode = result;
};

// 暴露函数
defineExpose({
  handleDialogOpen,
});
</script>

<template>
  <el-dialog
    :title="dialogConfig.type == 'add' ? '新增自定义表单' : '编辑自定义表单'"
    v-model="visible"
    width="30%"
    :close-on-click-modal="false"
  >
    <el-form :model="formData" ref="ruleRef" :rules="rules" label-width="100px">
      <el-form-item label="表单key" prop="formKey">
        <el-input
          v-model="formData.formKey"
          v-no-space
          :maxlength="40"
          placeholder="请输入表单key"
        />
      </el-form-item>
      <el-form-item label="表单名" prop="formName">
        <el-input
          v-model="formData.formName"
          v-no-space
          :maxlength="20"
          placeholder="请输入表单名"
        />
      </el-form-item>
      <el-form-item label="所属系统" prop="serviceCode">
        <el-select
          v-model="serviceCode"
          placeholder="请选择所属系统"
          style="width: 100%"
          multiple
          @change="systemChange"
        >
          <el-option
            v-for="item in systemList"
            :key="item.id"
            :label="item.dictLabel"
            :value="item.dictValue"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="表单类型" prop="formType">
        <el-select
          v-model="formData.formType"
          placeholder="请选择表单类型"
          style="width: 100%"
        >
          <el-option
            v-for="item in FORM_TYPE"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input
          type="textarea"
          v-model="formData.remark"
          :rows="2"
          show-word-limit
          :maxlength="200"
        />
      </el-form-item>
      <el-form-item label="状态" prop="state">
        <el-radio-group v-model="formData.state">
          <el-radio
            v-for="item in FORM_STATUS"
            :key="item.value"
            :label="item.value"
            >{{ item.label }}</el-radio
          >
        </el-radio-group>
      </el-form-item>
      <el-form-item label="表单地址" prop="formUrl">
        <el-input v-model="formData.formUrl" placeholder="请输入表单地址" />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleSubmit()"
          :disabled="buttonLoading"
        >
          提交
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<style scoped></style>
